import type { AxiosError, AxiosResponse } from 'axios'
import type { IResponseApi } from '@/modules/Core/interfaces/api/IApis'
import { BaseService } from '@/modules/Core/services/BaseService'
import type {
  IPortAgenda,
  IPortConfiguracion,
  IPortLogo,
  IPortModulos
} from '@/modules/Portal/interfaces'

// Endpoints: /api/v1/portal/configuracion/*
// Siempre sobre la entidad de la sesión: ninguna llamada envía un código de entidad.
export class ConfiguracionPortalService extends BaseService<IPortConfiguracion> {
  constructor() {
    super({ baseUrl: 'portal/configuracion/' })
  }

  async getConfiguracion(): Promise<
    AxiosResponse<IResponseApi<IPortConfiguracion>>
  > {
    return this.http
      .get<IResponseApi<IPortConfiguracion>>('/view')
      .catch(this.errorHandle)
  }

  async getModulos(): Promise<AxiosResponse<IResponseApi<IPortModulos>>> {
    try {
      const reg = await this.http.get<IResponseApi<IPortModulos>>('/modulos')
      return reg
    } catch (error) {
      return await this.errorHandle(error as AxiosError)
    }
  }

  async guardarModulos(
    modulos: string[]
  ): Promise<AxiosResponse<IResponseApi<IPortModulos>>> {
    try {
      const reg = await this.http.put<IResponseApi<IPortModulos>>(
        '/update-modulos',
        { modulos }
      )
      return reg
    } catch (error) {
      return await this.errorHandle(error as AxiosError)
    }
  }

  /** Logo en base64 para la vista previa (404 si no hay o ya no existe en Drive). */
  async getLogo(): Promise<AxiosResponse<IResponseApi<IPortLogo>>> {
    return this.http.get<IResponseApi<IPortLogo>>('/logo')
  }

  /** `null` o vacío deja a la entidad sin subdominio. */
  async guardarSubdominio(
    coenPortalSubdominio: string | null
  ): Promise<AxiosResponse<IResponseApi<IPortConfiguracion>>> {
    return this.http
      .put<
        IResponseApi<IPortConfiguracion>
      >('/update', { coenPortalSubdominio })
      .catch(this.errorHandle)
  }

  async subirLogo(
    archivo: File
  ): Promise<AxiosResponse<IResponseApi<IPortConfiguracion>>> {
    const formData = new FormData()
    formData.append('file', archivo)
    return this.http
      .post<IResponseApi<IPortConfiguracion>>('/upload-logo', formData)
      .catch(this.errorHandle)
  }

  async quitarLogo(): Promise<AxiosResponse<IResponseApi<IPortConfiguracion>>> {
    return this.http
      .delete<IResponseApi<IPortConfiguracion>>('/delete-logo')
      .catch(this.errorHandle)
  }

  /** Servicios que el paciente puede agendar desde el portal y máximo de citas agendadas. */
  async getAgenda(): Promise<AxiosResponse<IResponseApi<IPortAgenda>>> {
    return this.http
      .get<IResponseApi<IPortAgenda>>('/agenda')
      .catch(this.errorHandle)
  }

  async guardarAgenda(
    servicios: number[],
    maxCitas: number
  ): Promise<AxiosResponse<IResponseApi<IPortAgenda>>> {
    return this.http
      .put<IResponseApi<IPortAgenda>>('/update-agenda', { servicios, maxCitas })
      .catch(this.errorHandle)
  }
}
